import React, { ReactElement, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import Icon from '~/components/atom/Icon';

export type Props = {
  children: Array<ReactElement>;
  tabOptions?: Array<{
    /** Shows a check icon on the tab if it has a saved value */
    hasValue?: boolean;
    /** Shows an error icon on the tab if it has an error */
    hasError?: boolean;
  }>;
  onChange?: (index: number) => void;
};

const Tabs: React.FCC<Props> = ({
  dataTestId,
  children,
  onChange,
  tabOptions,
  ...rest
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [markerConfig, setMarkerConfig] = useState({ left: 0, width: 0 });
  const activeStepHeaderRef = useRef<HTMLButtonElement>(null);

  const initialActiveTab = (() => {
    if (!tabOptions) return 0;
    const tabWithSavedValue = tabOptions.findIndex(tab => tab.hasValue);
    if (tabWithSavedValue !== -1) return tabWithSavedValue;
    return 0;
  })();

  useEffect(() => {
    setActiveTab(initialActiveTab);
  }, [initialActiveTab]);

  useEffect(() => {
    if (activeStepHeaderRef?.current) {
      setMarkerConfig({
        left: activeStepHeaderRef.current.offsetLeft,
        width: activeStepHeaderRef.current.clientWidth,
      });
    }
  }, [activeTab, activeStepHeaderRef]);

  return (
    <Container data-testid={dataTestId} {...rest}>
      <HeaderContainer role="tablist">
        {React.Children.map(children, (child, index) => {
          if (child && typeof child === 'object' && 'props' in child) {
            const { label }: { label: string } = child.props;
            const active = index === activeTab;
            const tabOption = tabOptions?.[index];
            const { hasValue, hasError } = tabOption ?? {};
            const iconColor = hasError ? 'danger' : 'accent';

            if (label) {
              return (
                <Header
                  $active={active}
                  ref={active ? activeStepHeaderRef : null}
                  key={label}
                  onClick={() => {
                    setActiveTab(index);
                    if (onChange) onChange(index);
                  }}
                  role="tab"
                  tabIndex={index}
                  id={`tab_${index}_header`}
                  aria-selected={active}
                  aria-controls={`tab_${index}_body`}
                >
                  {hasValue && (
                    <StyledIcon
                      name={hasError ? 'alert-circle' : 'check'}
                      size="base"
                      color={{ group: iconColor, variant: 'dark' }}
                      background={{
                        group: iconColor,
                        variant: 'translucent',
                      }}
                      strokeWidth={2.2}
                    />
                  )}
                  {label}
                </Header>
              );
            }
          }

          return null;
        })}
        <ActiveMarker
          style={{
            transform: `translate3d(${markerConfig.left}px,0, 0)`,
            width: markerConfig.width + 'px',
          }}
        />
      </HeaderContainer>
      <>
        {React.Children.map(children, (child, index) => {
          if (index === activeTab)
            return (
              <Tab
                role="tabpanel"
                tabIndex={index}
                id={`tab_${index}_body`}
                aria-labelledby={`tab_${index}_header`}
              >
                {/* @ts-ignore */}
                {child.props.children}
              </Tab>
            );

          return null;
        })}
      </>
    </Container>
  );
};

const StyledIcon = styled(Icon)(
  ({}) => css`
    position: absolute;
    top: 2px;
    right: 0px;
  `,
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    border-bottom: 1px solid ${theme.color('tertiary')};
    margin-bottom: ${theme.space('base')};
  `,
);

const Header = styled.button<{ $active: boolean }>(
  ({ theme, $active }) => css`
    margin-left: ${theme.space('s')};
    padding: ${theme.space('s')};
    background-color: transparent;
    font-weight: ${theme.fontWeight('medium')};
    border: none;
    display: flex;
    transition: all 0.3s ease;
    position: relative;

    cursor: pointer;

    ${$active &&
    css`
      color: ${theme.color('secondary')};
    `}

    &:focus {
      color: ${theme.color('secondary')};
    }

    &:first-of-type {
      margin-left: 0;
    }
  `,
);

const ActiveMarker = styled.div(
  ({ theme }) => css`
    height: 5px;
    width: 0px;
    background-color: ${theme.color('secondary')};
    position: absolute;
    bottom: -1px;
    display: block;
    transition: all 0.3s ease;
  `,
);

const fadeIn = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`;

const Tab = styled.div`
  opacity: 0;
  animation: ${fadeIn} 725ms ease-out forwards;
`;

export default Tabs;
